import * as Yup from 'yup';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatUTCDateToLocal } from 'src/utilities';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/minimals/form';
import { PhoneInputCustom } from 'src/components/streametric/phone-input';
import { InfoIconV2 } from 'src/assets/icons/InfoIconV2';
import { AccountBillingInformationGetResponse } from 'src/types/account-billing-information';
import { BILLING_INFORMATION_TOOLTIPS } from '../config';
import { BillingAuthorizationLabel } from './BillingAuthorizationLabel';

export type AccountBillingInformationFormValues = {
  companyName: string;
  website: string;
  companyStreetAddress1: string;
  companyStreetAddress2: string;
  companyCity: string;
  companyState: string;
  companyZipCode: string;
  companyCountry: string;
  contactTitle: string;
  contactEmail: string;
  contactFirstName: string;
  contactMiddleName: string;
  contactLastName: string;
  contactPhoneNumber: string;
  contactMobileNumber: string;
  billingStreetAddress1: string;
  billingStreetAddress2: string;
  billingCity: string;
  billingState: string;
  billingZipCode: string;
  billingCountry: string;
  autoRenewal: boolean;
  authorizeBilling: boolean;
  useDifferentBillingAddress: boolean;
};

const schema = Yup.object().shape({
  companyName: Yup.string().required('Company name is required'),
  website: Yup.string().url('Please provide a valid URL'),
  companyStreetAddress1: Yup.string().required('Street address is required'),
  contactEmail: Yup.string()
    .required('Email is required')
    .email('Please provide a valid email address'),
  billingStreetAddress1: Yup.string().when('useDifferentBillingAddress', {
    is: true,
    then: (schema) => schema.required('Street address is required'),
  }),
  billingCity: Yup.string().when('useDifferentBillingAddress', {
    is: true,
    then: (schema) => schema.required('City is required'),
  }),
  billingState: Yup.string().when('useDifferentBillingAddress', {
    is: true,
    then: (schema) => schema.required('State is required'),
  }),
  billingZipCode: Yup.string().when('useDifferentBillingAddress', {
    is: true,
    then: (schema) => schema.required('Zip code is required'),
  }),
  billingCountry: Yup.string().when('useDifferentBillingAddress', {
    is: true,
    then: (schema) => schema.required('Country is required'),
  }),
});

const getDefaultValues = (
  data?: AccountBillingInformationGetResponse
): AccountBillingInformationFormValues => ({
  companyName: data?.companyInformation?.companyName ?? '',
  website: data?.companyInformation?.website ?? '',
  companyStreetAddress1: data?.companyInformation?.streetAddress1 ?? '',
  companyStreetAddress2: data?.companyInformation?.streetAddress2 ?? '',
  companyCity: data?.companyInformation?.city ?? '',
  companyState: data?.companyInformation?.state ?? '',
  companyZipCode: data?.companyInformation?.zipCode ?? '',
  companyCountry: data?.companyInformation?.country ?? '',
  contactTitle: data?.billingContact?.title ?? '',
  contactEmail: data?.billingContact?.email ?? '',
  contactFirstName: data?.billingContact?.firstName ?? '',
  contactMiddleName: data?.billingContact?.middleName ?? '',
  contactLastName: data?.billingContact?.lastName ?? '',
  contactPhoneNumber: data?.billingContact?.phoneNumber ?? '',
  contactMobileNumber: data?.billingContact?.mobileNumber ?? '',
  billingStreetAddress1: data?.billingAddress?.streetAddress1 ?? '',
  billingStreetAddress2: data?.billingAddress?.streetAddress2 ?? '',
  billingCity: data?.billingAddress?.city ?? '',
  billingState: data?.billingAddress?.state ?? '',
  billingZipCode: data?.billingAddress?.zipCode ?? '',
  billingCountry: data?.billingAddress?.country ?? '',
  autoRenewal: data?.autoRenewal ?? false,
  authorizeBilling: data?.authorizeBilling ?? false,
  useDifferentBillingAddress: !!data?.billingAddress,
});

type Props = {
  defaultValues?: AccountBillingInformationGetResponse;
  isLoading: boolean;
  readOnly?: boolean;
  onSubmit: (values: AccountBillingInformationFormValues) => void;
};

// TODO: set up a global MUI theme override for Tooltip styles across the app, then remove this constant and its usages
const TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'white',
      color: 'text.primary',
      boxShadow: '0 0 2px 0 rgba(145,158,171,0.24), -20px 20px 40px -4px rgba(145,158,171,0.24)',
    },
  },
};

export const AccountBillingInformationForm = ({
  defaultValues,
  isLoading,
  readOnly = false,
  onSubmit,
}: Props) => {
  const methods = useForm<AccountBillingInformationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(),
    mode: 'onTouched',
  });

  const { handleSubmit, reset, control, setValue, getValues, watch, formState } = methods;
  const useDifferentBillingAddress = watch('useDifferentBillingAddress');
  const authorizeBilling = watch('authorizeBilling');
  const { dirtyFields, isValid, isSubmitting } = formState;
  const isDirty = Object.keys(dirtyFields).some((key) => key !== 'authorizeBilling');

  const showFooter = !readOnly || !!defaultValues?.updatedAt;

  const saveDisabledReason = !isDirty
    ? BILLING_INFORMATION_TOOLTIPS.SAVE_NO_CHANGES
    : !isValid
    ? BILLING_INFORMATION_TOOLTIPS.SAVE_VALIDATION_ERRORS
    : !authorizeBilling
    ? BILLING_INFORMATION_TOOLTIPS.SAVE_AUTHORIZE_BILLING
    : undefined;

  useEffect(() => {
    if (!defaultValues) return;
    reset(getDefaultValues(defaultValues));
  }, [defaultValues, reset]);

  useEffect(() => {
    const sub = watch((_values, { name }) => {
      // reset() fires with name === undefined; skip so it can set values correctly
      if (name === undefined) return;
      if (name === 'authorizeBilling') return;

      const otherDirty = Object.keys(dirtyFields).some((k) => k !== 'authorizeBilling');
      const nextValue = otherDirty ? false : defaultValues?.authorizeBilling ?? false;

      if (getValues('authorizeBilling') !== nextValue) {
        setValue('authorizeBilling', nextValue);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue, getValues, dirtyFields, defaultValues]);

  const handleBillingAddressToggle = (checked: boolean) => {
    setValue('useDifferentBillingAddress', checked, { shouldDirty: true });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{ p: 3, mt: 3, mb: 8, bgcolor: 'background.default' }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Company Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyName"
              label="Company Name"
              required
              onChange={(e) => setValue('companyName', e.target.value, { shouldDirty: true })}
              testId="company-name-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="website"
              label="Website"
              onChange={(e) => setValue('website', e.target.value, { shouldDirty: true })}
              testId="website-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyStreetAddress1"
              label="Street Address 1"
              required
              onChange={(e) =>
                setValue('companyStreetAddress1', e.target.value, { shouldDirty: true })
              }
              testId="company-street-address-1-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyStreetAddress2"
              label="Street Address 2"
              onChange={(e) =>
                setValue('companyStreetAddress2', e.target.value, { shouldDirty: true })
              }
              testId="company-street-address-2-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyCity"
              label="City"
              onChange={(e) => setValue('companyCity', e.target.value, { shouldDirty: true })}
              testId="company-city-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyState"
              label="State"
              onChange={(e) => setValue('companyState', e.target.value, { shouldDirty: true })}
              testId="company-state-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyZipCode"
              label="Zip Code"
              onChange={(e) => setValue('companyZipCode', e.target.value, { shouldDirty: true })}
              testId="company-zip-code-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="companyCountry"
              label="Country"
              onChange={(e) => setValue('companyCountry', e.target.value, { shouldDirty: true })}
              testId="company-country-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 3 }}>
          Billing Contact
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="contactTitle"
              label="Title"
              onChange={(e) => setValue('contactTitle', e.target.value, { shouldDirty: true })}
              testId="contact-title-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="contactEmail"
              label="Email"
              required
              onChange={(e) => setValue('contactEmail', e.target.value, { shouldDirty: true })}
              testId="contact-email-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="contactFirstName"
              label="First Name"
              onChange={(e) => setValue('contactFirstName', e.target.value, { shouldDirty: true })}
              testId="contact-first-name-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="contactMiddleName"
              label="Middle Name"
              onChange={(e) => setValue('contactMiddleName', e.target.value, { shouldDirty: true })}
              testId="contact-middle-name-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="contactLastName"
              label="Last Name"
              onChange={(e) => setValue('contactLastName', e.target.value, { shouldDirty: true })}
              testId="contact-last-name-input"
              disabled={isLoading || isSubmitting || readOnly}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="contactPhoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInputCustom
                  value={field.value}
                  onChange={field.onChange}
                  size="small"
                  disabled={isLoading || isSubmitting || readOnly}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="contactMobileNumber"
              control={control}
              render={({ field }) => (
                <PhoneInputCustom
                  value={field.value}
                  onChange={field.onChange}
                  label="Mobile number"
                  size="small"
                  disabled={isLoading || isSubmitting || readOnly}
                />
              )}
            />
          </Grid>
        </Grid>

        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={useDifferentBillingAddress}
                onChange={(e) => handleBillingAddressToggle(e.target.checked)}
                data-sm="use-different-billing-address-toggle"
                disabled={isLoading || isSubmitting || readOnly}
              />
            }
            label="Use Different Billing Address"
            labelPlacement="end"
            sx={{ mr: 0 }}
          />
          <Tooltip
            placement="top"
            componentsProps={TOOLTIP_PROPS}
            title={
              <Typography sx={{ maxWidth: 500, whiteSpace: 'normal' }}>
                {BILLING_INFORMATION_TOOLTIPS.USE_DIFFERENT_BILLING_ADDRESS}
              </Typography>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIconV2 />
            </Box>
          </Tooltip>
        </Stack>

        {useDifferentBillingAddress && (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Billing Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingStreetAddress1"
                  label="Street Address 1"
                  required
                  onChange={(e) =>
                    setValue('billingStreetAddress1', e.target.value, { shouldDirty: true })
                  }
                  testId="billing-street-address-1-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingStreetAddress2"
                  label="Street Address 2"
                  onChange={(e) =>
                    setValue('billingStreetAddress2', e.target.value, { shouldDirty: true })
                  }
                  testId="billing-street-address-2-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingCity"
                  label="City"
                  required
                  onChange={(e) => setValue('billingCity', e.target.value, { shouldDirty: true })}
                  testId="billing-city-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingState"
                  label="State"
                  required
                  onChange={(e) => setValue('billingState', e.target.value, { shouldDirty: true })}
                  testId="billing-state-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingZipCode"
                  label="Zip Code"
                  required
                  onChange={(e) =>
                    setValue('billingZipCode', e.target.value, { shouldDirty: true })
                  }
                  testId="billing-zip-code-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="billingCountry"
                  label="Country"
                  required
                  onChange={(e) =>
                    setValue('billingCountry', e.target.value, { shouldDirty: true })
                  }
                  testId="billing-country-input"
                  disabled={isLoading || isSubmitting || readOnly}
                  size="small"
                />
              </Grid>
            </Grid>
          </>
        )}

        <Stack direction="column" sx={{ mt: 4 }} gap={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <RHFSwitch
              name="autoRenewal"
              testId="auto-renewal-toggle"
              label="Auto Renewal"
              disabled={isLoading || isSubmitting || readOnly}
              handleChange={(_, checked) => setValue('autoRenewal', checked, { shouldDirty: true })}
            />
            <Tooltip
              placement="top"
              componentsProps={TOOLTIP_PROPS}
              title={
                <Typography sx={{ maxWidth: 500, whiteSpace: 'normal' }}>
                  {BILLING_INFORMATION_TOOLTIPS.AUTO_RENEWAL}
                </Typography>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIconV2 />
              </Box>
            </Tooltip>
          </Stack>
          <Controller
            name="authorizeBilling"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    size="small"
                    disabled={!isDirty || isLoading || isSubmitting || readOnly}
                  />
                }
                label={<BillingAuthorizationLabel />}
              />
            )}
          />
          {showFooter && <Divider />}
          {showFooter && (
            <Stack
              justifyContent="flex-end"
              flexDirection="row"
              alignItems="center"
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: { xs: 2, md: 3 },
              }}
            >
              {defaultValues?.updatedAt && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Last edited: &nbsp;
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {formatUTCDateToLocal(false, defaultValues.updatedAt)}
                  </Typography>
                  {defaultValues.updatedBy?.type === 'User' && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        &nbsp;by&nbsp;
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {defaultValues.updatedBy.firstName} {defaultValues.updatedBy.lastName}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              {!readOnly && (
                <Stack flexDirection="row" gap={{ xs: 1, md: 2 }}>
                  <Button
                    variant="outlined"
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                    data-sm="discard-button"
                    disabled={!isDirty || isSubmitting}
                    onClick={() => reset(getDefaultValues(defaultValues))}
                  >
                    Discard
                  </Button>
                  <Tooltip title={saveDisabledReason ?? ''} componentsProps={TOOLTIP_PROPS}>
                    <span style={{ cursor: saveDisabledReason ? 'not-allowed' : 'default' }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        sx={{
                          width: { xs: '100%', md: 'auto' },
                          pointerEvents: saveDisabledReason ? 'none' : undefined,
                        }}
                        data-sm="save-changes-button"
                        disabled={!isDirty || !authorizeBilling || !isValid || isSubmitting}
                      >
                        Save Changes
                      </LoadingButton>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Card>
    </FormProvider>
  );
};
